import type { QuoteLocalPlanDefinition } from '../pages/cpq-v2/state/types';
import type { QuoteLocalPackageDefinitionModel } from './quoteMapper';

export type LoginCompositionOwner = { companyId: string; id: string };
export type LoginCompositionArchive = {
  /** Fingerprint of the recipe and resource version at the time it was archived. */
  packages: Record<string, string>;
  plans: Record<string, string>;
};
export type LoginCompositionLibrary = {
  generation: string;
  packages: QuoteLocalPackageDefinitionModel[];
  plans: QuoteLocalPlanDefinition[];
  /** Archived recipes stay in the library for bindings and existing plan dependencies. */
  archived?: LoginCompositionArchive;
  bindings: Record<
    string,
    {
      packageIds: Record<string, string>;
      planIds: Record<string, string>;
    }
  >;
};
type StoredLibrary = LoginCompositionLibrary & { owner: string };
const KEY = 'quotation-system:login-compositions:v1';
const EVENT = 'quotation-system:login-compositions-changed';
let fallback: StoredLibrary | null = null;
const ownerKey = (owner: LoginCompositionOwner) =>
  `${owner.companyId}:${owner.id}`;

const emptyArchive = (): LoginCompositionArchive => ({
  packages: {},
  plans: {},
});

const normalizedMembers = (
  members: readonly { deviceId: string; quantity: number }[],
) => {
  const totals = new Map<string, number>();
  for (const member of members)
    totals.set(
      member.deviceId,
      (totals.get(member.deviceId) ?? 0) + member.quantity,
    );
  return [...totals]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([deviceId, quantity]) => ({ deviceId, quantity }));
};

/**
 * A stable identity for detecting edits while an async company save is in flight.
 * Names are intentionally excluded from composition comparison, but the recipe
 * resource version and nested package resource versions detect intervening edits.
 */
export function loginCompositionRecipeFingerprint(
  library: Pick<LoginCompositionLibrary, 'packages' | 'plans'>,
  kind: 'package' | 'plan',
  definitionId: string,
): string | null {
  if (kind === 'package') {
    const recipe = library.packages.find((entry) => entry.id === definitionId);
    return recipe
      ? JSON.stringify({
          kind,
          id: recipe.id,
          resourceVersion: recipe.resourceVersion,
          packageGroupId: recipe.packageGroupId,
          mergePricesOverride: recipe.mergePricesOverride,
          packageGroupRevisionId: recipe.packageGroupSnapshot?.revisionId,
          members: normalizedMembers(recipe.members),
        })
      : null;
  }
  const recipe = library.plans.find((entry) => entry.id === definitionId);
  if (!recipe) return null;
  const packageById = new Map(
    library.packages.map((entry) => [entry.id, entry]),
  );
  return JSON.stringify({
    kind,
    id: recipe.id,
    resourceVersion: recipe.resourceVersion,
    discountPercent: Number(recipe.discountPercent.toFixed(2)),
    members: recipe.members
      .map((member) => {
        const source = member.source;
        return source.type === 'local-package'
          ? {
              occurrenceId: member.occurrenceId,
              quantityPerSet: member.quantityPerSet,
              source,
              package: (() => {
                const dependency = packageById.get(source.definitionId);
                return dependency
                  ? {
                      resourceVersion: dependency.resourceVersion,
                      packageGroupId: dependency.packageGroupId,
                      mergePricesOverride: dependency.mergePricesOverride,
                      packageGroupRevisionId:
                        dependency.packageGroupSnapshot?.revisionId,
                      members: normalizedMembers(dependency.members),
                    }
                  : null;
              })(),
            }
          : {
              occurrenceId: member.occurrenceId,
              quantityPerSet: member.quantityPerSet,
              source,
            };
      })
      .sort((left, right) =>
        left.occurrenceId < right.occurrenceId
          ? -1
          : left.occurrenceId > right.occurrenceId
            ? 1
            : 0,
      ),
  });
}

function validArchive(value: unknown): LoginCompositionArchive {
  if (!value || typeof value !== 'object') return emptyArchive();
  const archive = value as Partial<LoginCompositionArchive>;
  const records = (record: unknown): Record<string, string> =>
    record && typeof record === 'object' && !Array.isArray(record)
      ? Object.fromEntries(
          Object.entries(record).filter(
            ([id, fingerprint]) =>
              id.length > 0 && typeof fingerprint === 'string',
          ),
        )
      : {};
  return {
    packages: records(archive.packages),
    plans: records(archive.plans),
  };
}

function currentArchive(value: {
  packages: QuoteLocalPackageDefinitionModel[];
  plans: QuoteLocalPlanDefinition[];
  archived?: LoginCompositionArchive;
}): LoginCompositionArchive {
  const archive = validArchive(value.archived);
  const packages = Object.fromEntries(
    Object.entries(archive.packages).filter(
      ([id, fingerprint]) =>
        loginCompositionRecipeFingerprint(value, 'package', id) === fingerprint,
    ),
  );
  const plans = Object.fromEntries(
    Object.entries(archive.plans).filter(
      ([id, fingerprint]) =>
        loginCompositionRecipeFingerprint(value, 'plan', id) === fingerprint,
    ),
  );
  return { packages, plans };
}

function stored(): StoredLibrary | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as StoredLibrary;
    if (
      typeof value.owner !== 'string' ||
      typeof value.generation !== 'string' ||
      !Array.isArray(value.packages) ||
      !Array.isArray(value.plans) ||
      !value.bindings ||
      typeof value.bindings !== 'object'
    )
      return null;
    if (
      value.packages.some((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        const mergePricesOverride = (entry as { mergePricesOverride?: unknown })
          .mergePricesOverride;
        return (
          mergePricesOverride !== undefined &&
          mergePricesOverride !== null &&
          typeof mergePricesOverride !== 'boolean'
        );
      })
    )
      return null;
    return {
      ...value,
      archived: currentArchive(value),
    };
  } catch {
    return fallback;
  }
}

/** Persist recipes only. Quote instances, equipment facts and credentials never enter this library. */
function recipeOnly(value: LoginCompositionLibrary): LoginCompositionLibrary {
  return {
    generation: value.generation,
    archived: currentArchive(value),
    packages: value.packages.map((entry) => ({
      id: entry.id,
      name: entry.name,
      sortOrder: entry.sortOrder,
      resourceVersion: entry.resourceVersion,
      ...(entry.mergePricesOverride !== undefined
        ? { mergePricesOverride: entry.mergePricesOverride }
        : {}),
      ...(entry.packageGroupId !== undefined
        ? { packageGroupId: entry.packageGroupId }
        : {}),
      ...(entry.packageGroupSnapshot !== undefined
        ? { packageGroupSnapshot: structuredClone(entry.packageGroupSnapshot) }
        : {}),
      members: entry.members.map(({ deviceId, quantity }) => ({
        deviceId,
        quantity,
      })),
    })),
    plans: value.plans.map((entry) => ({
      id: entry.id,
      name: entry.name,
      sortOrder: entry.sortOrder,
      resourceVersion: entry.resourceVersion,
      discountPercent: entry.discountPercent,
      members: entry.members.map((member) => ({
        occurrenceId: member.occurrenceId,
        quantityPerSet: member.quantityPerSet,
        source:
          member.source.type === 'catalog-package'
            ? { type: 'catalog-package', packageId: member.source.packageId }
            : {
                type: 'local-package',
                definitionId: member.source.definitionId,
              },
      })),
    })),
    bindings: Object.fromEntries(
      Object.entries(value.bindings).map(([id, binding]) => [
        id,
        {
          packageIds: { ...binding.packageIds },
          planIds: { ...binding.planIds },
        },
      ]),
    ),
  };
}

function persist(value: StoredLibrary | null) {
  fallback = value;
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
  } catch {
    // The active login can still use the in-memory library when browser storage is unavailable.
  }
  window.dispatchEvent(new Event(EVENT));
}

/** Delete one login-local recipe; package recipes stay while any local plan depends on them. */
export function deleteLoginCompositionDefinition(
  owner: LoginCompositionOwner,
  generation: string,
  kind: 'package' | 'plan',
  definitionId: string,
): LoginCompositionLibrary {
  const current = stored();
  if (
    !current ||
    current.owner !== ownerKey(owner) ||
    current.generation !== generation
  )
    throw new Error('登录已变化，临时方案和套餐列表已重置，请重新打开');
  const library = recipeOnly(current);
  const definitions = kind === 'package' ? library.packages : library.plans;
  const recipe = definitions.find((entry) => entry.id === definitionId);
  if (!recipe) throw new Error('临时定义已变化，请刷新后重试');
  if (kind === 'package') {
    const dependentPlan = library.plans.find((plan) =>
      plan.members.some(
        (member) =>
          member.source.type === 'local-package' &&
          member.source.definitionId === definitionId,
      ),
    );
    if (dependentPlan)
      throw new Error(`临时套餐仍被方案“${dependentPlan.name}”绑定，不能删除`);
  }
  const bindingMapKey = kind === 'package' ? 'packageIds' : 'planIds';
  const recipeKeyPrefix = `${recipe.id}@`;
  const next = {
    ...library,
    packages:
      kind === 'package'
        ? library.packages.filter((entry) => entry.id !== definitionId)
        : library.packages,
    plans:
      kind === 'plan'
        ? library.plans.filter((entry) => entry.id !== definitionId)
        : library.plans,
    bindings: Object.fromEntries(
      Object.entries(library.bindings).map(([versionId, binding]) => [
        versionId,
        {
          ...binding,
          [bindingMapKey]: Object.fromEntries(
            Object.entries(binding[bindingMapKey]).filter(
              ([recipeKey]) => !recipeKey.startsWith(recipeKeyPrefix),
            ),
          ),
        },
      ]),
    ),
  };
  persist({ ...next, owner: ownerKey(owner) });
  return recipeOnly(next);
}

export function beginLoginCompositionLibrary(
  owner: LoginCompositionOwner,
): LoginCompositionLibrary {
  const value: StoredLibrary = {
    owner: ownerKey(owner),
    generation: crypto.randomUUID(),
    packages: [],
    plans: [],
    archived: emptyArchive(),
    bindings: {},
  };
  persist(value);
  return recipeOnly(value);
}

export function readLoginCompositionLibrary(
  owner: LoginCompositionOwner,
): LoginCompositionLibrary {
  const value = stored();
  if (!value || value.owner !== ownerKey(owner))
    return {
      generation: '',
      packages: [],
      plans: [],
      archived: emptyArchive(),
      bindings: {},
    };
  try {
    return recipeOnly(value);
  } catch {
    return {
      generation: '',
      packages: [],
      plans: [],
      archived: emptyArchive(),
      bindings: {},
    };
  }
}

/** Called only after the actual login owner has been verified, never by a stale tab's storage listener. */
export function restoreLoginCompositionLibrary(owner: LoginCompositionOwner) {
  const current = readLoginCompositionLibrary(owner);
  return current.generation ? current : beginLoginCompositionLibrary(owner);
}

export function writeLoginCompositionLibrary(
  owner: LoginCompositionOwner,
  value: LoginCompositionLibrary,
) {
  const current = stored();
  if (
    !current ||
    current.owner !== ownerKey(owner) ||
    current.generation !== value.generation
  )
    throw new Error('登录已变化，临时方案和套餐列表已重置，请重新打开');
  const currentArchiveState = currentArchive(current);
  const incomingArchiveState = validArchive(value.archived);
  persist({
    ...recipeOnly({
      ...value,
      archived: {
        packages: {
          ...currentArchiveState.packages,
          ...incomingArchiveState.packages,
        },
        plans: {
          ...currentArchiveState.plans,
          ...incomingArchiveState.plans,
        },
      },
    }),
    owner: ownerKey(owner),
  });
}

export type LoginCompositionSavedSnapshot =
  | {
      kind: 'package';
      members: readonly { deviceId: string; quantity: number }[];
    }
  | {
      kind: 'plan';
      discountPercent: number;
      catalogPackages: readonly {
        packageId: string;
        members: readonly { deviceId: string; quantity: number }[];
      }[];
      occurrences: readonly {
        quantityPerSet: number;
        members: readonly { deviceId: string; quantity: number }[];
      }[];
    };

export type ArchiveLoginCompositionResult =
  | { archived: true }
  | {
      archived: false;
      reason:
        | 'login-changed'
        | 'recipe-changed'
        | 'recipe-missing'
        | 'composition-mismatch';
    };

function validMembers(
  members: readonly { deviceId: string; quantity: number }[],
): boolean {
  return members.every(
    ({ deviceId, quantity }) =>
      typeof deviceId === 'string' &&
      deviceId.length > 0 &&
      Number.isSafeInteger(quantity) &&
      quantity > 0,
  );
}

function memberTotals(
  members: readonly { deviceId: string; quantity: number }[],
  multiplier = 1,
): Map<string, number> | null {
  if (
    !Number.isSafeInteger(multiplier) ||
    multiplier < 1 ||
    !validMembers(members)
  )
    return null;
  const totals = new Map<string, number>();
  for (const { deviceId, quantity } of members) {
    const next = (totals.get(deviceId) ?? 0) + quantity * multiplier;
    if (!Number.isSafeInteger(next)) return null;
    totals.set(deviceId, next);
  }
  return totals;
}

function totalsEqual(
  left: ReadonlyMap<string, number>,
  right: ReadonlyMap<string, number>,
): boolean {
  return (
    left.size === right.size &&
    [...left].every(([deviceId, quantity]) => right.get(deviceId) === quantity)
  );
}

function recipeMatchesSnapshot(
  library: LoginCompositionLibrary,
  kind: 'package' | 'plan',
  definitionId: string,
  snapshot: LoginCompositionSavedSnapshot,
): boolean {
  if (kind !== snapshot.kind) return false;
  if (kind === 'package' && snapshot.kind === 'package') {
    const recipe = library.packages.find((entry) => entry.id === definitionId);
    const expected = recipe && memberTotals(recipe.members);
    const saved = memberTotals(snapshot.members);
    return Boolean(expected && saved && totalsEqual(expected, saved));
  }
  if (kind !== 'plan' || snapshot.kind !== 'plan') return false;
  const recipe = library.plans.find((entry) => entry.id === definitionId);
  if (
    !recipe ||
    !Number.isFinite(snapshot.discountPercent) ||
    Number(recipe.discountPercent.toFixed(2)) !==
      Number(snapshot.discountPercent.toFixed(2))
  )
    return false;
  const packages = new Map(library.packages.map((entry) => [entry.id, entry]));
  const catalogPackages = new Map(
    snapshot.catalogPackages.map((entry) => [entry.packageId, entry.members]),
  );
  const expected = new Map<string, number>();
  for (const member of recipe.members) {
    const packageMembers =
      member.source.type === 'local-package'
        ? packages.get(member.source.definitionId)?.members
        : catalogPackages.get(member.source.packageId);
    if (!packageMembers) return false;
    const totals = memberTotals(packageMembers, member.quantityPerSet);
    if (!totals) return false;
    for (const [deviceId, quantity] of totals) {
      const next = (expected.get(deviceId) ?? 0) + quantity;
      if (!Number.isSafeInteger(next)) return false;
      expected.set(deviceId, next);
    }
  }
  const saved = new Map<string, number>();
  for (const occurrence of snapshot.occurrences) {
    const totals = memberTotals(occurrence.members, occurrence.quantityPerSet);
    if (!totals) return false;
    for (const [deviceId, quantity] of totals) {
      const next = (saved.get(deviceId) ?? 0) + quantity;
      if (!Number.isSafeInteger(next)) return false;
      saved.set(deviceId, next);
    }
  }
  return totalsEqual(expected, saved);
}

/** Archive a recipe only after a successful company save, while retaining it for bindings and dependencies. */
export function archiveLoginCompositionIfExact(input: {
  owner: LoginCompositionOwner;
  generation: string;
  kind: 'package' | 'plan';
  definitionId: string;
  expectedFingerprint: string;
  saved: LoginCompositionSavedSnapshot;
}): ArchiveLoginCompositionResult {
  const current = stored();
  if (
    !current ||
    current.owner !== ownerKey(input.owner) ||
    current.generation !== input.generation
  )
    return { archived: false, reason: 'login-changed' };
  const fingerprint = loginCompositionRecipeFingerprint(
    current,
    input.kind,
    input.definitionId,
  );
  if (fingerprint === null)
    return { archived: false, reason: 'recipe-missing' };
  if (fingerprint !== input.expectedFingerprint)
    return { archived: false, reason: 'recipe-changed' };
  const library = recipeOnly(current);
  if (
    !recipeMatchesSnapshot(library, input.kind, input.definitionId, input.saved)
  )
    return { archived: false, reason: 'composition-mismatch' };
  const currentArchiveState = library.archived ?? emptyArchive();
  const archived = {
    ...currentArchiveState,
    [input.kind === 'package' ? 'packages' : 'plans']: {
      ...currentArchiveState[input.kind === 'package' ? 'packages' : 'plans'],
      [input.definitionId]: fingerprint,
    },
  };
  persist({ ...library, archived, owner: ownerKey(input.owner) });
  return { archived: true };
}

export function isLoginCompositionArchived(
  library: LoginCompositionLibrary,
  kind: 'package' | 'plan',
  definitionId: string,
): boolean {
  const archive = library.archived?.[kind === 'package' ? 'packages' : 'plans'];
  return Boolean(
    archive?.[definitionId] &&
      archive[definitionId] ===
        loginCompositionRecipeFingerprint(library, kind, definitionId),
  );
}

/** Picker lists hide archived recipes; keep the original library for materialization and dependency resolution. */
export function visibleLoginCompositionDefinitions(
  library: LoginCompositionLibrary,
): Pick<LoginCompositionLibrary, 'packages' | 'plans'> {
  return {
    packages: library.packages.filter(
      (entry) => !isLoginCompositionArchived(library, 'package', entry.id),
    ),
    plans: library.plans.filter(
      (entry) => !isLoginCompositionArchived(library, 'plan', entry.id),
    ),
  };
}

export function archivedLoginCompositionDefinitionIds(
  library: LoginCompositionLibrary,
  kind: 'package' | 'plan',
): ReadonlySet<string> {
  const definitions = kind === 'package' ? library.packages : library.plans;
  return new Set(
    definitions
      .filter((entry) => isLoginCompositionArchived(library, kind, entry.id))
      .map((entry) => entry.id),
  );
}

export function clearLoginCompositionLibrary(owner?: LoginCompositionOwner) {
  if (owner && stored()?.owner !== ownerKey(owner)) return;
  persist(null);
}

export function subscribeLoginCompositionLibrary(
  listener: () => void,
): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === KEY || event.key === null) listener();
  };
  window.addEventListener(EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}
